import { useConfirm } from '@hprofits/ui';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { toValue } from 'vue';
import { RouteLocation } from 'vue-router';

import { t } from '@/i18n';
import { UsePageControllerResult } from '@/modules/common/state/usePageController';

// TODO: Why Copy?!
/**
 * Copy arrives as a value OR a getter. A plain string is resolved when the caller runs — for a
 * composable that is component setup — and then stored, so a language switch never reaches it: the
 * dialog opened afterwards still shows the old wording. Taking `MaybeRefOrGetter` lets a caller
 * pass `() => t(key)` and have it read at the moment the copy is actually shown. Static strings
 * stay valid for callers with nothing to translate.
 */
export type EntityDeleteDialogConfirmCopy = {
  title: MaybeRefOrGetter<string>;
  description: (displayName: string) => string;
  confirmText?: MaybeRefOrGetter<string>;
};

export type RecoverableErrorBranch<TVars, TContext = void> = {
  matches: (err: unknown) => boolean;
  confirm: {
    // Same late-resolution contract as `confirmCopy`: this dialog opens on a recovery path, long
    // after the composable was created, so a resolved string would arrive in the old language even
    // though the user switched before the first dialog.
    title: MaybeRefOrGetter<string>;
    description: string | ((ctx: TContext, displayName: string) => string);
    confirmText: MaybeRefOrGetter<string>;
  };
  retryWith: (originalVars: TVars, ctx: TContext) => TVars;
};

export type UseEntityDeleteDialogParams<TVars, TData = void, TContext = void> = {
  resolveDisplayName: (ctx: TContext) => string;
  /**
   * Required. The confirm dialog used to fall back to a sentence assembled from an entity noun,
   * which no catalogue can express and which silently served English. Every caller owns its copy.
   */
  confirmCopy: EntityDeleteDialogConfirmCopy;
  buildVars: (ctx: TContext) => TVars | null;
  mutate: (vars: TVars) => Promise<TData>;
  isPending: Readonly<Ref<boolean>>;
  buildErrorMessage: (err: unknown) => string;
  successMessage?: MaybeRefOrGetter<string>;
  onConfirmed?: () => Promise<void> | void;
  onSuccess?: (data: TData, vars: TVars) => Promise<void> | void;
  recoverableErrors?: ReadonlyArray<RecoverableErrorBranch<TVars, TContext>>;
  pageController: UsePageControllerResult;
};

export type UseEntityDeleteDialogReturn<TContext = void> = {
  openDeleteDialog: (ctx: TContext) => Promise<void>;
  isDeleting: Readonly<Ref<boolean>>;
};

export type DeletedEntityRedirectContext<TParams> = TParams &
  Readonly<{
    redirectLocation: RouteLocation;
  }>;

/** Internal marker: distinguishes a failed `mutate` from a failed post-success step. */
class MutationFailedError extends Error {
  constructor(readonly original: unknown) {
    super('entity delete mutation failed');
  }
}

export const useEntityDeleteDialog = <TVars, TData = void, TContext = void>(
  params: UseEntityDeleteDialogParams<TVars, TData, TContext>,
): UseEntityDeleteDialogReturn<TContext> => {
  const {
    resolveDisplayName,
    confirmCopy,
    buildVars,
    mutate,
    isPending,
    buildErrorMessage,
    successMessage,
    onConfirmed,
    onSuccess,
    recoverableErrors,
    pageController: { errors, notifications },
  } = params;

  const confirm = useConfirm();

  const applySuccess = async (data: TData, vars: TVars): Promise<void> => {
    if (successMessage) {
      notifications.showNotification(toValue(successMessage));
    }
    if (onSuccess) {
      await onSuccess(data, vars);
    }
  };

  // Mutation errors and post-success errors take DIFFERENT paths: only a failing `mutate`
  // may enter the recoverable-error flow — a throwing `applySuccess` (success toast /
  // `onSuccess` navigation) after a SUCCEEDED delete must never render a delete-failure
  // message or, worse, match a recoverable branch and re-run the mutation. `applySuccess`
  // errors bubble to `openDeleteDialog`'s last-resort net instead.
  const runDelete = async (vars: TVars): Promise<void> => {
    let data: TData;
    try {
      data = await mutate(vars);
    } catch (err) {
      throw new MutationFailedError(err);
    }
    await applySuccess(data, vars);
  };

  const tryRecover = async (err: unknown, originalVars: TVars, ctx: TContext): Promise<void> => {
    const branch = recoverableErrors?.find((b) => b.matches(err));
    if (!branch) {
      errors.showError(buildErrorMessage(err));
      return;
    }
    const resolvedDescription =
      typeof branch.confirm.description === 'function'
        ? branch.confirm.description(ctx, resolveDisplayName(ctx))
        : branch.confirm.description;
    const forceConfirmed = await confirm({
      title: toValue(branch.confirm.title),
      description: resolvedDescription,
      confirmText: toValue(branch.confirm.confirmText),
      confirmColor: 'red',
    });
    if (!forceConfirmed) {
      return;
    }
    try {
      await runDelete(branch.retryWith(originalVars, ctx));
    } catch (retryErr) {
      if (retryErr instanceof MutationFailedError) {
        errors.showError(buildErrorMessage(retryErr.original));
        return;
      }
      // Post-success failure after a SUCCEEDED retry — escalate to the last-resort net.
      throw retryErr;
    }
  };

  const promptConfirm = async (displayName: string): Promise<boolean> => {
    const confirmText = toValue(confirmCopy.confirmText) ?? t('modules.campaigns.actions.delete');
    return confirm({
      title: toValue(confirmCopy.title),
      description: confirmCopy.description(displayName),
      confirmText,
      confirmColor: 'red',
    });
  };

  const openDeleteDialog = async (ctx: TContext): Promise<void> => {
    try {
      const vars = buildVars(ctx);
      if (vars === null) {
        return;
      }
      if (!(await promptConfirm(resolveDisplayName(ctx)))) {
        return;
      }
      if (onConfirmed) {
        await onConfirmed();
      }
      try {
        await runDelete(vars);
      } catch (err) {
        if (err instanceof MutationFailedError) {
          await tryRecover(err.original, vars, ctx);
        } else {
          // Post-success failure (toast / onSuccess navigation) after a SUCCEEDED delete —
          // never a recovery candidate; fall through to the last-resort net.
          throw err;
        }
      }
    } catch (err) {
      // Last-resort net so `openDeleteDialog` NEVER rejects into a fire-and-forget
      // caller: covers the dialog infrastructure itself (buildVars, the confirm
      // mechanism, onConfirmed, a throwing recover branch) and post-success failures —
      // MUTATION failures are already routed through tryRecover above.
      errors.showError(buildErrorMessage(err));
    }
  };

  return { openDeleteDialog, isDeleting: isPending };
};
